/**
 * The TallyPrime connection, as the Receivables data screens need it
 * (docs/tally/09). Feature branch only.
 *
 * Everything here goes through the database functions of 0034, as the person
 * signed in: each checks their capability (`datasource.view` to look,
 * `datasource.manage` to change, `datasource.sync` for "sync now"). The
 * browser never sees a credential or a hash — `connector_status` returns
 * neither — and the pairing code is shown once, here, and stored only as a
 * hash.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AccountCheck, PreviewIntelligence, SwitchExisting } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { userFacingRefusal } from '../lib/refusal';

export interface DiscoveredCompany { name: string; guid: string; booksFrom: string | null }

export interface PreviewAccount {
    sourceKey: string;
    name: string;
    total: number;
    type: 'Dr' | 'Cr';
    gstin: string | null;
    phone: string | null;
    email: string | null;
    /** The ledger's group in TallyPrime (previews stored from docs/tally/16 on). */
    group?: string | null;
    /** This account's accounting checks (previews stored from docs/tally/16 on). */
    checks?: AccountCheck[];
}

export interface ConnectorPreview {
    summary: {
        accounts: number;
        receivable: number;
        creditAccounts: number;
        creditAmount: number;
        ageing: Record<string, number>;
        unaged: number;
    };
    diagnostics: string[];
    asOf: string;
    company: { id: string; name: string };
    /** Only for those who manage the data source. */
    accounts?: PreviewAccount[];
    /**
     * What Timely will make of the receivables: the position, the ageing
     * histogram and the accounting checks, naming no customer (docs/tally/16).
     * Absent from a preview stored before it existed.
     */
    intelligence?: PreviewIntelligence;
}

export interface ConnectorReport {
    tallyReachable?: boolean;
    companyLoaded?: boolean;
    lastError?: string | null;
    tallyListensOnNetwork?: boolean | null;
}

export interface ConnectorStatus {
    id: string;
    status: 'pending' | 'active' | 'revoked';
    deviceName: string;
    sourceCompany: { id: string; name: string } | null;
    connectorVersion?: string | null;
    pairedAt?: string;
    lastSeenAt?: string | null;
    lastSuccessAt?: string | null;
    lastAppliedAt?: string | null;
    lastError?: string | null;
    lastReport?: ConnectorReport | null;
    syncRequestedAt?: string | null;
    activatedAt?: string | null;
    credentialIssuedAt?: string | null;
    rotateRequestedAt?: string | null;
    discoveredCompanies?: DiscoveredCompany[] | null;
    discoveredAt?: string | null;
    preview?: ConnectorPreview | null;
    previewAt?: string | null;
    heldSettlement?: { settle: number; open: number; accounts?: { company: string; amount: number }[]; at?: string } | null;
    heldSettlementRead?: string | null;
    replacedSheet?: boolean;
    switchSettlements?: number;
    revokedAt?: string | null;
}

/**
 * The connector's status: re-read every `pollMs` (setup moves on its own as
 * the connector checks in), and on demand. Null: there has never been one.
 */
export function useConnector(workspaceId: string, pollMs: number | null) {
    const [status, setStatus] = useState<ConnectorStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const issuedFor = useRef(workspaceId);

    const reload = useCallback(async () => {
        issuedFor.current = workspaceId;
        const { data, error: failed } = await supabase().rpc('connector_status', { p_workspace: workspaceId });
        if (issuedFor.current !== workspaceId) return;
        if (failed) setError('The TallyPrime connection could not be read just now.');
        else { setError(null); setStatus((data as ConnectorStatus | null) ?? null); }
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { setLoading(true); void reload(); }, [reload]);
    useEffect(() => {
        if (!pollMs) return;
        const timer = setInterval(() => { void reload(); }, pollMs);
        return () => clearInterval(timer);
    }, [pollMs, reload]);

    // Whether this company is in the TallyPrime pilot (Timely's switch, server-side). Unknown or refused counts
    // as no: TallyPrime is then not offered, and the database refuses every step of it anyway.
    const [pilot, setPilot] = useState<boolean | null>(null);
    const [pilotAsked, setPilotAsked] = useState(0);
    useEffect(() => {
        let current = true;
        void supabase().rpc('connector_pilot', { p_workspace: workspaceId }).then(({ data, error: refused }) => {
            if (current) setPilot(!refused && data === true);
        });
        return () => { current = false; };
    }, [workspaceId, pilotAsked]);
    useEffect(() => { setPilot(null); }, [workspaceId]);

    /** The company switches TallyPrime on for itself (0040: whoever may manage receivables data), then asks again. */
    const turnOn = useCallback(async () => {
        const { error: refused } = await supabase().rpc('tally_turn_on', { p_workspace: workspaceId });
        if (refused) throw new Error('TallyPrime could not be switched on just now. Try again in a moment.');
        setPilotAsked(n => n + 1);
    }, [workspaceId]);

    return { status, loading: loading || pilot === null, error, reload, pilot: pilot === true, turnOn };
}

async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase().rpc(fn, args);
    // The database's own refusals are sentences written for the person reading them; Postgres's words never are.
    if (error) throw new Error(userFacingRefusal(error));
    return data as T;
}

export async function createPairingCode(workspaceId: string): Promise<{ code: string; expiresAt: string }> {
    const rows = await call<{ code: string; expires_at: string }[]>('connector_create_pairing_code', { p_workspace: workspaceId });
    const row = rows?.[0];
    if (!row) throw new Error('A pairing code could not be made just now.');
    return { code: row.code, expiresAt: row.expires_at };
}

export const chooseCompany = (workspaceId: string, connectorId: string, guid: string) =>
    call<void>('connector_choose_company', { p_workspace: workspaceId, p_connector: connectorId, p_company_guid: guid });

export const activateConnector = (
    workspaceId: string, connectorId: string, guid: string,
    bindings: { account: string; sourceKey: string }[], settle: string[], replaceSheet: boolean,
) => call<{ matched: number; settling: number; replacedSheet: boolean }>('connector_activate', {
    p_workspace: workspaceId, p_connector: connectorId, p_confirm_company_guid: guid,
    p_bindings: bindings, p_settle: settle, p_replace_sheet: replaceSheet,
});

export const revokeConnector = (workspaceId: string, connectorId: string) =>
    call<void>('connector_revoke', { p_workspace: workspaceId, p_connector: connectorId });

export const requestSync = (workspaceId: string) => call<boolean>('connector_request_sync', { p_workspace: workspaceId });

export const requestRotation = (workspaceId: string) => call<boolean>('connector_request_rotation', { p_workspace: workspaceId });

export const renameConnector = (workspaceId: string, connectorId: string, name: string) =>
    call<void>('connector_rename', { p_workspace: workspaceId, p_connector: connectorId, p_name: name });

export const approveSettlement = (workspaceId: string, connectorId: string, read: string) =>
    call<void>('connector_approve_settlement', { p_workspace: workspaceId, p_connector: connectorId, p_read: read });

interface AccountRow {
    id: string; company: string; source_key: string | null; total: number | string | null; total_type: 'Dr' | 'Cr' | null;
    tax_id: string | null; contact_number: string | null; email: string | null; settled_at: string | null;
}

/** The book's accounts, as the switch review compares them with TallyPrime's ledgers. Paged: a book can be large. */
export async function loadAccountsForSwitch(workspaceId: string): Promise<SwitchExisting[]> {
    const out: SwitchExisting[] = [];
    for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase().from('customers')
            .select('id, company, source_key, total, total_type, tax_id, contact_number, email, settled_at')
            .eq('workspace_id', workspaceId).order('id').range(from, from + 999);
        if (error) throw new Error('The accounts could not be read for the review.');
        const rows = (data ?? []) as AccountRow[];
        for (const r of rows) {
            out.push({
                id: r.id, company: r.company, sourceKey: r.source_key, total: Number(r.total ?? 0) || 0,
                ...(r.total_type ? { totalType: r.total_type } : {}), ...(r.settled_at ? { settledAt: r.settled_at } : {}),
                taxId: r.tax_id, contactNumber: r.contact_number, email: r.email,
            });
        }
        if (rows.length < 1000) return out;
    }
}

/** How many accounts carry a TallyPrime identity (they would not match a sheet's keys). A GET of no rows, never HEAD (tests/noHeadRequests). */
export async function countTallyAccounts(workspaceId: string): Promise<number> {
    const { count, error } = await supabase().from('customers').select('id', { count: 'exact' })
        .eq('workspace_id', workspaceId).like('source_key', 'tally:%').limit(0);
    if (error) return 0;
    return count ?? 0;
}
